import React, { useContext } from 'react';
import { Panel } from '../../../components/containers/Panel';
import classes from './Publishing.module.scss';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Select } from '../../../components/forms/Select';
import {
  FbLinkPreview,
  GetWorkspaceLazyQueryHookResult,
  ImportMediaBehavior,
  MinimumRecycleTime,
  PostMultiMediaAttachMode,
  PostQueuePosition
} from '../../../models/graphql';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { Tooltip } from '../../../components/actions/Tooltip';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { FormElementAction } from '../../../models/basic-objects';
import { BeaconContext } from '../../../providers/BeaconProvider';
import classNames from 'classnames';

export type PublishingViewProps = {
  attributionInstagramToggleAction: ToggleAction;
  evergreenSelectAction: FormElementAction<boolean>;
  fbLinkPreviewSelectAction: FormElementAction<FbLinkPreview>;
  importMediaBehaviorSelectAction: FormElementAction<ImportMediaBehavior>;
  isLoading: boolean;
  minRecycleTimeSelectAction: FormElementAction<MinimumRecycleTime>;
  multiMediaAttachModeSelectAction: FormElementAction<PostMultiMediaAttachMode>;
  queuePositionSelectAction: FormElementAction<PostQueuePosition>;
  rules: RegisterOptions;
  toggleRules: RegisterOptions;
  useFormInstance: UseFormReturn;
  workspaceSettingsResults: GetWorkspaceLazyQueryHookResult[1];
};

export const PublishingView = (props: PublishingViewProps): JSX.Element => {
  const settings = props.workspaceSettingsResults.data?.workspace?.settings;
  const headerTitle = 'Publishing Settings';
  const { beacon } = useContext(BeaconContext);

  return (
    <CenteredContentLayout loading={props.isLoading} headerTitle={headerTitle} hasSidebar>
      <Panel>
        <PanelHeader
          title={'Default Publishing Options'}
          subTitle={
            'When you create a new post, the Post Editor will start with the following options already selected. You can always customize the settings for individual posts.'
          }
        />
        <div className={'margin-bottom-small-32 margin-top-small-16 margin-top-small-16 margin-top-large-24'}>
          <div className={'grid-x padding-bottom-small-16 padding-bottom-large-32 '}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Evergreen Recycling
              <div className={'hide-for-small show-for-large'}>
                <Tooltip pointer target={'#evergreenTooltip'} type={'inlined'}>
                  If the majority of your posts contain Evergreen content that you want to recycle, you can save time by
                  enabling recycling for newly-created posts.
                </Tooltip>
              </div>
            </div>
            <div className={classNames('cell small-12 large-6')}>
              <Select
                items={[
                  {
                    label: "Don't Recycle After Last Post",
                    value: 'false'
                  },
                  {
                    label: 'Recycle After Last Post',
                    value: 'true'
                  }
                ]}
                name={'evergreenRecycling'}
                selectedValue={
                  settings?.evergreenRecycling !== undefined ? `${settings.evergreenRecycling}` : undefined
                }
                size={'large'}
                action={props.evergreenSelectAction as unknown as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
          <div className={'grid-x padding-bottom-small-16 padding-bottom-large-32'}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Minimum Time to Recycle
              <div className={'hide-for-small show-for-large'}>
                <Tooltip pointer target={'#minTimeToRecycleTooltip'} type={'inlined'}>
                  If you set a post to recycle automatically according to your Posting Plan, we can prevent posts from
                  being recycled too frequently. This can happen accidentally if you have too many time slots and not
                  enough content.
                </Tooltip>
              </div>
            </div>
            <div className={'cell small-12 large-6'}>
              <Select
                items={[
                  {
                    label: '1 Week',
                    value: MinimumRecycleTime.WEEKS_1
                  }
                ]}
                selectedValue={settings?.minimumRecycleTime}
                name={'minimumRecycleTime'}
                size={'large'}
                action={props.minRecycleTimeSelectAction as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
          <div className={'grid-x padding-bottom-small-16 padding-bottom-large-32'}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Media
              <div className={'hide-for-small show-for-large'}>
                <Tooltip autoHide={false} pointer target={'#mediaTooltip'} type={'inlined'}>
                  <div>
                    When you add multiple images/videos to a post, you can either decide to publish them all in one go
                    (as a multi-image post), or if you&apos;re recycling the post over multiple cycles, you can vary the
                    media each time it&apos;s recycled. <a onClick={() => beacon('article', '687', { type: 'modal' })}>Learn more</a>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className={'cell small-12 large-6'}>
              <Select
                items={[
                  {
                    label: 'Publish All Images/Videos In One Post',
                    value: PostMultiMediaAttachMode.MULTIPLE
                  },
                  {
                    label: 'Vary Media each Cycle',
                    value: PostMultiMediaAttachMode.ALTERNATE
                  }
                ]}
                selectedValue={settings?.multiMediaAttachMode}
                name={'multiMediaAttachMode'}
                size={'large'}
                action={props.multiMediaAttachModeSelectAction as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
          <div className={'grid-x padding-bottom-small-16 padding-bottom-large-32'}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Queue Position
              <div className={'hide-for-small show-for-large'}>
                <Tooltip pointer target={'#scheduleTooltip'} type={'inlined'}>
                  This applies when using your Posting Plan to publish posts based on your weekly Timeslots. You can
                  decide whether new posts will be added to the top of the Queue (for publishing sooner), or to the
                  bottom of the Queue (to be published after your existing Queued content).
                </Tooltip>
              </div>
            </div>
            <div className={'cell small-12 large-6'}>
              <Select
                items={[
                  {
                    label: 'Top of Queue',
                    value: PostQueuePosition.TOP
                  },
                  {
                    label: 'Bottom of Queue',
                    value: PostQueuePosition.BOTTOM
                  }
                ]}
                selectedValue={settings?.defaultQueuePosition}
                name={'queuePosition'}
                size={'large'}
                action={props.queuePositionSelectAction as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
          <div className={'grid-x padding-bottom-small-16 padding-bottom-large-32'}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Facebook Link Preview
              <div className={'hide-for-small show-for-large'}>
                <Tooltip pointer target={'#facebookLinkPreviewTooltip'} type={'inlined'}>
                  Facebook automatically generates a preview for links in posts, featuring a thumbnail, title,
                  description, and URL. Use this option to hide the preview, displaying only the link text in your
                  post&apos;s caption.
                </Tooltip>
              </div>
            </div>
            <div className={'cell small-12 large-6'}>
              <Select
                items={[
                  {
                    label: 'Keep Link Preview',
                    value: FbLinkPreview.USE
                  },
                  {
                    label: 'Hide Link Preview',
                    value: FbLinkPreview.REMOVE
                  }
                ]}
                selectedValue={settings?.fbLinkPreview}
                name={'fbLinkPreview'}
                size={'large'}
                action={props.fbLinkPreviewSelectAction as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
          <div className={'grid-x'}>
            <div className={`cell grid-x align-middle small-12 large-6 ${classes.Label}`}>
              Attachments (When Importing Posts)
              <div className={'hide-for-small show-for-large'}>
                <Tooltip
                  pointer
                  target={'#imagesTooltip'}
                  type={'inlined'}
                  position={window.innerHeight < 1000 ? 'top' : 'right'}>
                  <div>
                    Customize how images and link previews are displayed in your posts when importing content:
                    <ul>
                      <li>
                        <strong>Remove Images:</strong> Ideal for avoiding copyrighted imagery or emphasizing link
                        previews. Removes any attached images, making link previews visible instead.
                      </li>
                      <li>
                        <strong>Use Images:</strong> Utilizes attached images in posts. If a post includes a link, the
                        image will replace the link preview.
                      </li>
                      <li>
                        <strong>Fetch Image from Link:</strong> Searches the linked page for a primary image to attach
                        to your post. Useful when no image is attached, but suppresses link previews. Note: This may
                        infringe on copyright laws if the image is licensed.
                      </li>
                    </ul>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className={'cell small-12 large-6'}>
              <Select
                items={[
                  {
                    label: 'Remove Attached Images',
                    value: ImportMediaBehavior.REMOVE
                  },
                  {
                    label: 'Use Attached Images',
                    value: ImportMediaBehavior.USE
                  },
                  {
                    label: 'Fetch Image from Link',
                    value: ImportMediaBehavior.USE_FROM_LINK
                  }
                ]}
                selectedValue={settings?.importMediaBehavior}
                name={'importMediaBehavior'}
                size={'large'}
                action={props.importMediaBehaviorSelectAction as FormElementAction}
                rules={props.rules}
                useFormInstance={props.useFormInstance}
              />
            </div>
          </div>
        </div>
      </Panel>
    </CenteredContentLayout>
  );
};
